import { styled } from '@linaria/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { FloraSpinner } from '../flora-spinner';
import { Text } from '../../../../ui-library/typography/typography';
import type { Message, ToolApproval, ToolApprovalDecision } from '../../flora.type';
import { isTurnFeedbackEligible } from '../flora-feedback/flora-feedback.helpers';
import { getPlainTextContent, hasDisplayableContent } from '../flora-message/flora-message.helpers';
import { AgentMessageBubble } from './agent-message-bubble';
import { AgentMessageListScrollContext } from './agent-message-list-scroll.context';
import { AgentTurnRail, type TurnRailItem } from './agent-turn-rail';

/** Minimum number of turns before the navigation rail is shown. */
const MIN_TURNS_FOR_RAIL = 2;
/** Pixels below the scroll top used to decide which turn is "in view". */
const ACTIVE_TURN_OFFSET = 80;
/**
 * Space reserved above a jumped-to turn so it clears the sticky chat header
 * (~44px) with a little breathing room, instead of landing behind it.
 */
const JUMP_HEADER_OFFSET = 56;
/**
 * Distance (px) from the bottom within which the viewport counts as "at the
 * bottom" for re-engaging stick-to-bottom. Generous enough to absorb sub-pixel
 * rounding and a small streamed chunk without the follow disengaging.
 */
const STICK_THRESHOLD = 40;

interface AgentMessageListProps {
  questions: Message[];
  messages: Message[];
  isWaitingForFirstResponse: boolean;
  isActiveQuestion: boolean;
  loadingMessage: string;
  chatName?: string;
  chatHistoryId?: string;
  latestTurnIsLive?: boolean;
  themeToggle?: ReactNode;
  showFollowups?: boolean;
  enableTurnRail?: boolean;
  onPreviewDocument?: (documentId: string) => void;
  onFollowupClick?: (prompt: string) => void;
  onAnswerSubmit?: (jobId: string, answers: Record<string, string | string[]>) => void;
  onToolApproval?: (approvalDecision: ToolApprovalDecision, toolApproval: ToolApproval) => void;
}

export function AgentMessageList({
  questions,
  messages,
  isWaitingForFirstResponse,
  isActiveQuestion,
  loadingMessage,
  chatName,
  chatHistoryId,
  latestTurnIsLive,
  themeToggle,
  showFollowups = true,
  enableTurnRail = false,
  onPreviewDocument,
  onFollowupClick,
  onAnswerSubmit,
  onToolApproval,
}: AgentMessageListProps) {
  const endRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const scrollRafRef = useRef<number | null>(null);
  // Whether auto-scroll is stuck to the bottom; set by direction, not an
  // absolute at-bottom measurement (see handleScroll). Starts true so a
  // freshly opened chat lands on its latest message.
  const stickRef = useRef(true);
  const lastScrollTopRef = useRef(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTurn, setActiveTurn] = useState(0);

  // Pair each question with its answer by turnId rather than array position —
  // a turn with no persisted/streamed reply yet must not shift every later
  // turn's answer onto the wrong question. Falls back to positional lookup
  // only for a Message that somehow lacks a turnId.
  const answersByTurnId = useMemo(() => {
    const map = new Map<string, Message>();
    for (const message of messages) {
      if (message.turnId) map.set(message.turnId, message);
    }
    return map;
  }, [messages]);
  /**
   * Resolves the answer for a given question by its `turnId`, falling back to
   * positional lookup only when the question has no `turnId` at all.
   *
   * @param {Message} question - The user question to find an answer for
   * @param {number} index - The question's array position, used only as the fallback lookup
   * @returns {Message | undefined} The paired answer, if one exists yet
   */
  const answerForQuestion = useCallback(
    (question: Message, index: number): Message | undefined =>
      question.turnId ? answersByTurnId.get(question.turnId) : messages[index],
    [answersByTurnId, messages],
  );

  const turnRailItems = useMemo<TurnRailItem[]>(
    () =>
      questions.map((question, index) => {
        const answer = answerForQuestion(question, index);
        return {
          index,
          prompt: getPlainTextContent(question),
          preview: answer && hasDisplayableContent(answer) ? getPlainTextContent(answer) : '',
        };
      }),
    [questions, answerForQuestion],
  );

  const showTurnRail = enableTurnRail && turnRailItems.length >= MIN_TURNS_FOR_RAIL;

  const measureScrollState = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    setIsScrolled(el.scrollTop > 0);

    const atBottom = el.scrollTop >= el.scrollHeight - el.clientHeight - 4;

    const rows = el.querySelectorAll<HTMLElement>('[data-turn]');
    if (rows.length === 0) return;
    let idx = 0;
    if (atBottom) {
      idx = rows.length - 1;
    } else {
      const threshold = el.scrollTop + ACTIVE_TURN_OFFSET;
      rows.forEach((row, i) => {
        if (row.offsetTop <= threshold) idx = i;
      });
    }
    setActiveTurn((prev) => (prev === idx ? prev : idx));
  }, []);

  // Coalesce scroll work to one measurement per frame — the handler reads
  // layout (offsetTop) for every turn, which is costly to run per scroll tick.
  const handleScroll = useCallback(() => {
    // Maintain the stick flag synchronously from scroll DIRECTION, not from an
    // absolute at-bottom test (which races with streaming growth and latches the
    // follow off — the bug behind "it stutters then gives up"). Growth fires no
    // scroll event and our scroll-to-bottom only moves down, so the only way
    // scrollTop shrinks is a real upward user scroll → disengage. Returning to
    // (near) the bottom re-engages. The rAF below defers only the costlier
    // per-turn active-turn measurement.
    const el = listRef.current;
    if (el) {
      const { scrollTop, scrollHeight, clientHeight } = el;
      if (scrollTop < lastScrollTopRef.current - 1) stickRef.current = false;
      if (scrollHeight - clientHeight - scrollTop <= STICK_THRESHOLD) stickRef.current = true;
      lastScrollTopRef.current = scrollTop;
    }
    if (scrollRafRef.current !== null) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;
      measureScrollState();
    });
  }, [measureScrollState]);

  const handleJumpToTurn = useCallback((index: number) => {
    const el = listRef.current;
    if (!el) return;
    const row = el.querySelector<HTMLElement>(`[data-turn="${index}"]`);
    if (row) el.scrollTo({ top: Math.max(0, row.offsetTop - JUMP_HEADER_OFFSET), behavior: 'smooth' });
  }, []);

  useEffect(
    () => () => {
      if (scrollRafRef.current !== null) cancelAnimationFrame(scrollRafRef.current);
    },
    [],
  );

  const scrollToBottom = useCallback((smooth: boolean) => {
    endRef.current?.scrollIntoView?.({ behavior: smooth ? 'smooth' : 'auto', block: 'end' });
  }, []);

  // A ResizeObserver is used rather than keying off the `messages` array because
  // the live turn mutates its segments in place, so array identity may not
  // change on every streamed chunk and an effect dependency alone misses growth.
  useEffect(() => {
    const inner = innerRef.current;
    if (!inner || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      if (stickRef.current) scrollToBottom(false);
    });
    observer.observe(inner);
    return () => observer.disconnect();
  }, [scrollToBottom]);

  // Opening another chat lands on its latest message, even when the previous
  // chat was left scrolled up (the list is not remounted between chats).
  const previousChatIdRef = useRef(chatHistoryId);
  useEffect(() => {
    if (previousChatIdRef.current === chatHistoryId) return;
    previousChatIdRef.current = chatHistoryId;
    stickRef.current = true;
    scrollToBottom(false);
  }, [chatHistoryId, scrollToBottom]);

  // Read through a ref so the scroll context value stays stable across turns
  // (a new value would re-render every bubble consuming it).
  const isActiveQuestionRef = useRef(isActiveQuestion);
  useEffect(() => {
    isActiveQuestionRef.current = isActiveQuestion;
  }, [isActiveQuestion]);

  const scrollContextValue = useMemo(
    () => ({
      releaseStickToBottom: () => {
        stickRef.current = false;
      },
      releaseStickToBottomIfIdle: () => {
        if (!isActiveQuestionRef.current) stickRef.current = false;
      },
      revealElementTop: (element: HTMLElement) => {
        const list = listRef.current;
        if (!list) return;
        // While a live turn is being followed, the streaming answer wins.
        if (stickRef.current && isActiveQuestionRef.current) return;
        // The sticky header (full page only) sits inside the scroll container and
        // overlays the list's top edge, so the visible area starts below it.
        const visibleTop = list.getBoundingClientRect().top + (headerRef.current?.offsetHeight ?? 0);
        const offset = element.getBoundingClientRect().top - visibleTop;
        if (offset >= 0) return;
        // Release first so the follow (fired by the same layout change) doesn't
        // pull the view straight back to the bottom.
        stickRef.current = false;
        list.scrollTop += offset;
      },
    }),
    [],
  );

  // Instant while streaming so per-token animations don't queue up and lag.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally re-run on messages/questions identity and the streaming flags
  useEffect(() => {
    if (!stickRef.current) return;
    const isStreaming = isActiveQuestion && !isWaitingForFirstResponse;
    scrollToBottom(!isStreaming);
  }, [messages, questions, isActiveQuestion, isWaitingForFirstResponse]);

  return (
    <ListArea>
      {showTurnRail && <AgentTurnRail turns={turnRailItems} activeIndex={activeTurn} onJump={handleJumpToTurn} />}
      <ListContainer ref={listRef} onScroll={handleScroll}>
        {themeToggle && (
          <StickyHeader ref={headerRef} data-scrolled={isScrolled}>
            <HeaderInner>
              <ChatNameText size="medium" weight="bold" color="var(--ink-darkest)">
                {chatName || ''}
              </ChatNameText>
              {themeToggle}
            </HeaderInner>
          </StickyHeader>
        )}
        <MessagesInner ref={innerRef}>
          <AgentMessageListScrollContext.Provider value={scrollContextValue}>
            {questions.map((question, index) => {
              const answer = answerForQuestion(question, index);
              const hasAnswer = answer !== undefined && hasDisplayableContent(answer);
              const isLatestTurn = index === questions.length - 1;
              return (
                <TurnAnchor key={question.turnId ?? index} data-turn={index}>
                  <AgentMessageBubble
                    question={question}
                    answer={hasAnswer ? answer : undefined}
                    isStreaming={isActiveQuestion && !isWaitingForFirstResponse && isLatestTurn}
                    onPreviewDocument={onPreviewDocument}
                    onFollowupClick={onFollowupClick}
                    onAnswerSubmit={onAnswerSubmit}
                    onToolApproval={onToolApproval}
                    showFollowups={showFollowups && !isActiveQuestion && isLatestTurn}
                    chatHistoryId={chatHistoryId}
                    showFeedback={isTurnFeedbackEligible({
                      index,
                      questionsLength: questions.length,
                      isActiveQuestion,
                      isWaitingForFirstResponse,
                      latestTurnIsLive,
                      chatHistoryId,
                      message: answer,
                    })}
                  />
                </TurnAnchor>
              );
            })}

            {isWaitingForFirstResponse && (
              <WaitingRow>
                <FloraSpinner />
                <Text size="small" color="var(--ink-lighter)" style={{ fontStyle: 'italic' }}>
                  {loadingMessage}
                </Text>
              </WaitingRow>
            )}
          </AgentMessageListScrollContext.Provider>

          {/* Always rendered (even during a live turn) so stick-to-bottom
              auto-scroll has an anchor while segments stream in. */}
          <div ref={endRef} />
        </MessagesInner>
      </ListContainer>
    </ListArea>
  );
}

const ListArea = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const ListContainer = styled.div`
  position: relative;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

const TurnAnchor = styled.div`
  scroll-margin-top: 56px;
`;

const MessagesInner = styled.div`
  max-width: var(--content-max-width, 960px);
  margin: 0 auto;
  transition: max-width 0.25s ease;
  /* The side panel narrows the gutter via --flora-chat-gutter. */
  padding: 24px var(--flora-chat-gutter, 24px) 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const WaitingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 0;
`;

const StickyHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  background: var(--base-white);
  border-bottom: 1px solid transparent;
  transition: border-color 0.15s ease;

  &[data-scrolled='true'] {
    border-bottom-color: var(--sky-light);
  }
`;

const HeaderInner = styled.div`
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ChatNameText = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;
